import actions from "~/shared/actions";
import type { ForvaltningsForm } from "~/shared/components/investeringsvalg/forvaltningsformer";
import type { Spareprofil } from "~/shared/components/investeringsvalg/spareprofiler";
import type { Action } from "~/utils/actions";
import type { VeiledningsSvar } from "~/utils/kartlegging";

export interface KartleggingState {
	okonomi: Okonomivurdering;
	risiko: Risikovurdering;
	erfaring: Erfaringsvurdering;
	baerekraft: Baerekraftsvurdering;
	recommendation: VeiledningsOversikt;
	originalRecommendation: VeiledningsOversikt;
	selection: VeiledningsOversikt;
	recommendedShareProportion: number;
	prognose?: Prognose;
}

export interface VeiledningsOversikt {
	fondsskolekrav?: boolean;
	forvaltning: ForvaltningsForm;
	spareprofil: Spareprofil;
	sparebelop?: number;
	pensjonsniva?: number;
	nodvendigSparebelop?: number;
}

export type VeiledningsSporsmal<T = object, U = object> = {
	text: string;
	svar: VeiledningsSvarAlt<T>[];
} & U;

export type VeiledningsSvarAlt<T = object> = {
	id: VeiledningsSvar;
	text: string;
} & T;

interface Okonomivurdering {
	sporsmal: OkonomiSporsmalene;
	svar: OkonomiSvar;
	isLoading: boolean;
	isLoadingManedligSparing: boolean;
	engangsbelop?: number;
}

type OkonomiSporsmal = VeiledningsSporsmal<object, { toolTip?: string; extraText?: string; hvisNei?: string }>;

interface OkonomiSporsmalene {
	sparePerManed: OkonomiSporsmal;
	forbrukslan: OkonomiSporsmal;
	aksepterAnbefaling: OkonomiSporsmal;
	arslonn: OkonomiSporsmal;
	pensjonsniva: OkonomiSporsmal;
	bufferkonto: OkonomiSporsmal;
	bsu: OkonomiSporsmal;
}

export interface OkonomiSvar {
	sparePerManed: VeiledningsSvar;
	forbrukslan: VeiledningsSvar;
	aksepterAnbefaling?: boolean;
	arslonn: number;
	pensjonsniva: VeiledningsSvar;
	bufferkonto: VeiledningsSvar;
	bsu?: VeiledningsSvar;
}

interface Risikovurdering {
	sporsmal: { [key in RisikovurderingKey]: RisikovurderingSporsmal[] };
	mapping: { [key in RisikovurderingKey]: RisikovurderingMapping };
	svar: VeiledningsSvar[];
	complete: boolean;
}

type RisikovurderingKey = number | `e${number}` | "default";

export interface RisikovurderingSporsmal extends VeiledningsSporsmal<{ header: string }> {
	id: RisikovurderingKey;
	over50?: boolean;
}

export type RisikovurderingMapping = {
	[K in VeiledningsSvar]: Map | Mapping2;
};

type Mapping2 = {
	[K in VeiledningsSvar]: Map;
};

type Map = {
	[K in VeiledningsSvar]: Spareprofil;
};

interface Baerekraftsvurdering {
	sporsmal: VeiledningsSporsmal[];
	svar: VeiledningsSvar[];
	isLoading: boolean;
}

interface Erfaringsvurdering {
	sporsmal: VeiledningsSporsmal<{ header: string }>[];
	svar: VeiledningsSvar[];
	isLoading: boolean;
}

export interface Balance {
	expected: number;
	lowerLimit: number;
	upperLimit: number;
}

interface Basis {
	adminCost: number;
	managementCost: number;
	managementCostPercent: number;
	insuranceCost: number;
	savingAmount: number;
}

export interface SaldoOgKostnader {
	saldo: Balance;
	totaleKostnader: number;
}

export interface Prognose {
	avkastningAksjerPros: number;
	avkastningBankkontoPros: number;
	basis: Basis;
	inflasjonPros: number;
	prognosisAge?: number;
	saldoNaverdi: Balance;
	saldoReell: Balance;
	taxReimbursementPerYear: number;
	utbetaling: Balance;
	isLoading: boolean;
	utbetalingsPeriode: number;
	saldoOgKostnader1Ar?: SaldoOgKostnader;
	saldoOgKostnader5Ar?: SaldoOgKostnader;
}

const defaultState: KartleggingState = {
	okonomi: {
		svar: [],
	},
	risiko: {
		sporsmal: [],
		svar: [],
	},
	erfaring: {
		svar: [],
	},
	baerekraft: {},
} as unknown as KartleggingState;

export default function kartleggingReducer(state = defaultState, action: Action): KartleggingState {
	switch (action.type) {
		case actions.RESET_RISIKOVURDERING:
			return {
				...state,
				okonomi: {
					sporsmal: state.okonomi?.sporsmal,
					isLoading: state.okonomi?.isLoading,
					svar: [],
				} as unknown as Okonomivurdering,
				risiko: {
					...state.risiko,
					svar: [],
					complete: false,
				},
				erfaring: {
					...state.erfaring,
					svar: [],
				},
				baerekraft: {
					...state.baerekraft,
					svar: undefined as unknown as VeiledningsSvar[],
				},
				recommendation: undefined as unknown as VeiledningsOversikt,
				prognose: undefined as unknown as Prognose,
				selection: undefined as unknown as VeiledningsOversikt,
			};
		case actions.LOAD_RISIKOVURDERING_RESPONSE:
			return {
				...state,
				risiko: {
					...state.risiko,
					sporsmal: {
						...state.risiko.sporsmal,
						[action.data.productId || "default"]: action.data.sporsmal,
					},
					mapping: {
						...state.risiko.mapping,
						[action.data.productId || "default"]: action.data.mapping,
					},
				},
			};
		case actions.RISIKOVURDERING_COMPLETE:
			return {
				...state,
				risiko: {
					...state.risiko,
					complete: true,
				},
				recommendation: {
					...state.recommendation,
					...action.data.recommendation,
				},
				recommendedShareProportion: action.data.recommendedShareProportion,
				originalRecommendation: action.data.originalRecommendation,
				selection: undefined as unknown as VeiledningsOversikt,
			};
		case actions.SET_PROFILE_SELECTION:
			return { ...state, selection: action.data };
		default:
			return state;
	}
}
